import { Suspense } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { RequireAuth } from '../auth/RequireAuth';
import { AppShell } from './AppShell';
import { characterAreaRoutes } from './characterRoutes';
import { lazyWithReload } from './lazyWithReload';
import { FavoriteTabRedirect } from './FavoriteTabRedirect';
import { worldRedirectRoutes } from './worldRedirects';
// Beide sind winzig und bewusst KEIN Lazy-Chunk: die 404-Seite muss auch dann
// erscheinen, wenn genau das Chunk-Laden hakt (siehe lazyWithReload.ts).
import { DocumentTitle } from './DocumentTitle';
import { NotFound } from './NotFound';
import { RouteErrorBoundary } from './RouteErrorBoundary';
// R5-02: das Geruest fuer den Suspense-Fallback — bewusst KEIN Lazy-Chunk, es
// muss genau dann da sein, wenn ein anderer Chunk noch laedt.
import { RouteFallback } from './RouteFallback';
import { characterChatRouteImport, chatRouteImport, prefetchChatRoute } from './chatRoutePrefetch';

// Bequemer Zugriff fuer Aufrufer, die schon `app/routes` importieren.
export { prefetchChatRoute };

const LoginPage = lazyWithReload(() => import('../pages/login'));
const LegalPage = lazyWithReload(() => import('../pages/legal'));
// F88: Ziel des Bestätigungslinks aus der Registrierungsmail (öffentlich, Token per POST).
const VerifyEmailPage = lazyWithReload(() => import('../pages/verify-email'));
const KontaktPage = lazyWithReload(() => import('../pages/kontakt'));
const GruenderPage = lazyWithReload(() => import('../pages/gruender'));
// S11: Founders-Bereich mit echter Beteiligung (Abstimmungen, Rueckmeldungen, Credits).
const FoundersPage = lazyWithReload(() => import('../pages/founders/FoundersPage'));
const OnboardingPage = lazyWithReload(() => import('../pages/onboarding'));
const StartPage = lazyWithReload(() => import('../pages/start'));
const DashboardPage = lazyWithReload(() => import('../pages/dashboard'));
const CampaignNewPage = lazyWithReload(() => import('../pages/campaign-new'));
// W2-B/F-01 (E-06): Sofortstart an beiden Tueren, W2-B/X-09 (E-07): „Mehr".
const FirstScenePage = lazyWithReload(() => import('../pages/first-scene'));
const MorePage = lazyWithReload(() => import('../pages/more'));
// W1-C: die beiden neuen Tab-Flaechen. /chats traegt seit W4-B die echte
// Messenger-Liste (ChatList) + das Profil als Detail-Pane, /discover seit W4-C
// den oeffentlichen Katalog (Liste + Detail).
const ChatsPage = lazyWithReload(() => import('../pages/chats'));
const ChatProfilePane = lazyWithReload(() => import('../pages/chats/ProfilePane'));
const DiscoverPage = lazyWithReload(() => import('../pages/discover'));
// E5c (P25): /world ist eine Split-Ansicht (WorldSplit) mit Detail-Route.
const WorldSplit = lazyWithReload(() => import('../pages/world/WorldSplit'));
const WorldElementRoute = lazyWithReload(() => import('../pages/world/WorldElementRoute'));
const SpielbauPage = lazyWithReload(() => import('../pages/spielbau'));
const TableRoundsPage = lazyWithReload(() => import('../pages/table-rounds'));
const TableRoundNewPage = lazyWithReload(() => import('../pages/table-round-new'));
const SoundboardPage = lazyWithReload(() => import('../pages/soundboard'));
const CoinsPage = lazyWithReload(() => import('../pages/coins'));
const MembershipPage = lazyWithReload(() => import('../pages/membership'));
const MembershipOverviewPage = lazyWithReload(() => import('../pages/membership/OverviewPage'));
const SettingsPage = lazyWithReload(() => import('../pages/settings'));
// E5a' (P20): verschachtelte Split-Routen — Settings-Sektionen. Die
// Charakter-Routen (Liste/Editor/Detail) liegen seit R2-07 in characterRoutes.tsx.
// W4-C: Detail-Pane des Entdecken-Tabs (/discover/:id).
const DiscoverDetailPane = lazyWithReload(() => import('../pages/discover/DiscoverDetailPane'));
// R2-12: „Entdecken → Abenteuer" — fertige Abenteuer-Vorlagen zum Sofortstart.
const AdventuresPage = lazyWithReload(() => import('../pages/discover/adventures'));
const AdventureDetailPane = lazyWithReload(() => import('../pages/discover/adventures/AdventureDetailPane'));
const SettingsIndexRedirect = lazyWithReload(() =>
  import('../pages/settings/SettingsIndexRedirect').then((m) => ({ default: m.SettingsIndexRedirect })));
const SettingsSectionPane = lazyWithReload(() =>
  import('../pages/settings/SettingsSectionPane').then((m) => ({ default: m.SettingsSectionPane })));
const TindraPage = lazyWithReload(() => import('../pages/tindra'));
const MilestonesPage = lazyWithReload(() => import('../pages/milestones'));
// Paket S12 — Wochenrückblick und Erinnerungsbuch.
const WeekReviewPage = lazyWithReload(() => import('../pages/week-review'));
const MemoryBookPage = lazyWithReload(() => import('../pages/memory-book'));
// R5-02 (Diagnose B1-4, Befund 3): DIESELBE import()-Fabrik fuer die Lazy-Route
// UND fuer das Vorwaermen (chatRoutePrefetch.ts) — nur so trifft der Prefetch
// denselben Modul-Cache und der Chunk ist nach dem Klick schon da.
const ChatPage = lazyWithReload(chatRouteImport);
const CharacterChatPage = lazyWithReload(characterChatRouteImport);
const GroupChatPage = lazyWithReload(() => import('../pages/group-chat'));
const TableRoundChatPage = lazyWithReload(() => import('../pages/table-round-chat'));
const TableRoundSessionPage = lazyWithReload(() => import('../pages/table-round-session'));
const IntimateVideosPage = lazyWithReload(() => import('../pages/intimate-videos'));
const AdminPage = lazyWithReload(() => import('../pages/admin'));
const ModerationDeskPage = lazyWithReload(() => import('../pages/moderation'));
// AdminQueryScope ist winzig (QueryClient-Provider) und wird im selben Chunk
// geladen wie AdminPage — daher hier als normaler Import (kein eigenes lazy).
import { AdminQueryScope } from '../pages/admin/AdminQueryScope';

function ProfileRedirect() {
  const location = useLocation();
  return <Navigate to={{ pathname: '/settings', search: location.search }} replace />;
}

export function AppRoutes() {
  const location = useLocation();
  return (
    <>
      {/* Tab-Titel zentral, EINMAL gerendert — bewusst ausserhalb von Suspense
          und Boundary: so stimmt der Titel auch, waehrend ein Route-Chunk noch
          laedt oder eine Route in die Fehleranzeige gelaufen ist. */}
      <DocumentTitle />
    <RouteErrorBoundary resetKey={location.pathname}>
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          {/* Oeffentliche Seiten — bewusst OHNE RequireAuth erreichbar. */}
          <Route path="/legal/:doc" element={<LegalPage />} />
          <Route path="/kontakt" element={<KontaktPage />} />
          <Route path="/gruender" element={<GruenderPage />} />
          <Route path="/founders" element={<FoundersPage />} />
          {/* Onboarding ist Pre-Login-Flow — ausserhalb RequireAuth (wie Legacy). */}
          <Route path="/onboarding" element={<OnboardingPage />} />
          {/* Gastfläche „Probeszene" (Weltenschwelle P13) — bewusst OHNE
              RequireAuth, kein Storage; neben /onboarding im PUBLIC-Block. */}
          <Route path="/start" element={<StartPage />} />
        <Route
          element={(
            <RequireAuth>
              <AppShell />
            </RequireAuth>
          )}
        >
          <Route index element={<FavoriteTabRedirect />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/campaign-new" element={<CampaignNewPage />} />
          <Route path="/first-scene/:door" element={<FirstScenePage />} />
          <Route path="/more" element={<MorePage />} />
          {/* W1-C: /chats + /discover als SplitRoute mit `index → null` —
              dieselbe Mechanik wie /characters, damit die Zwei-Spalten-Ansicht
              auf dem Tablet ohne Sonderfall funktioniert und das Handy die
              Liste (nicht eine leere Detailflaeche) zeigt. Die Index-Erkennung
              laeuft in SplitRoute ueber die ROUTE, nicht ueber useOutlet
              (P20-Regel, SplitRoute.tsx:11-17). Die Detail-Kindroute von
              /discover haengt W4-C hier ein. */}
          {/* W4-B: /chats/:characterId ist das PROFIL der Figur (nicht der
              Chat). Der Chat selbst liegt bewusst ausserhalb der Shell unter
              /character-chat/:characterId bzw. /group-chat/:id. */}
          <Route path="/chats" element={<ChatsPage />}>
            <Route index element={null} />
            <Route path=":characterId" element={<ChatProfilePane />} />
          </Route>
          {/* R2-12: Abenteuer-Vorlagen. Eigene SplitRoute NEBEN /discover —
              `:key` ist ein Vorlagen-Schluessel (Text), `:id` oben eine
              numerische Charakter-Id. Der statische Pfad schlaegt im
              react-router-Ranking die dynamische `:id`-Kindroute. */}
          <Route path="/discover/adventures" element={<AdventuresPage />}>
            <Route index element={null} />
            <Route path=":key" element={<AdventureDetailPane />} />
          </Route>
          {/* W4-C: /discover = Liste | Detail. `:id` ist die oeffentliche
              Publikation (kein eigener Charakter) — read-only + Uebernehmen. */}
          <Route path="/discover" element={<DiscoverPage />}>
            <Route index element={null} />
            <Route path=":id" element={<DiscoverDetailPane />} />
          </Route>
          {/* R2-07 (D3 c): Die alte /characters-Split-Route ist aufgeloest —
              der Index leitet hart auf /chats, die Verwaltung lebt unter
              /characters/manage, Editor und Detail sind EIGENSTAENDIGE Routen
              (mounten die alte Liste also nicht mehr mit). Tabelle in
              characterRoutes.tsx, damit der Vertragstest dieselbe Quelle liest. */}
          {characterAreaRoutes}
          {/* E5c (P25): Weltenbau als EINE Split-Ansicht. /world = WorldSplit
              (Liste|Detail|Inspektor), :type/:elementId = Detail-Pane mit `key`
              in der ROUTE (docs/30 §8.2/§8.3). spielbau als Sonder-„Kategorie". */}
          <Route path="/world" element={<WorldSplit />}>
            <Route index element={null} />
            <Route path="spielbau" element={<SpielbauPage />} />
            <Route path=":type" element={null} />
            <Route path=":type/:elementId" element={<WorldElementRoute />} />
          </Route>
          {/* E5c Vorwärts-Redirects (ALT->NEU): /world-element-edit?id= bleibt
              DAUERHAFT (formatAiMessage-Vertrag); /world-tree + /spielbau leiten
              auf die kanonischen /world-URLs (docs/30 §8.4, geteilte Tabelle). */}
          {worldRedirectRoutes}
          <Route path="/table-rounds" element={<TableRoundsPage />} />
          <Route path="/table-rounds/new" element={<TableRoundNewPage />} />
          <Route path="/soundboard" element={<SoundboardPage />} />
          <Route path="/coins" element={<CoinsPage />} />
          <Route path="/mitgliedschaft" element={<MembershipPage />} />
          {/* S5: Abo-Zentrale „Dein Abo" — eigene Vollseite, neben der
              Paywall. Free-User landen auf der Paywall, Bestandsabos kommen
              hierhin (MemberCard unten bzw. Paywall-Link „Zu deinem Abo"). */}
          <Route path="/mitgliedschaft/uebersicht" element={<MembershipOverviewPage />} />
          {/* S10: Web→App-Einstieg. Universal/App-Links treffen /app?tier=…
              &duration=…&src=web_paywall; der SPA-Router leitet auf die
              Paywall-Seite um, die dieselben Query-Parameter liest. */}
          <Route path="/app" element={<Navigate to="/mitgliedschaft" replace />} />
          {/* /settings/notifications nutzt wie die anderen Sektionen die Detail-Pane. */}
          <Route path="/settings" element={<SettingsPage />}>
            <Route index element={<SettingsIndexRedirect />} />
            <Route path=":section" element={<SettingsSectionPane />} />
          </Route>
          <Route
            path="/admin"
            element={(
              <AdminQueryScope>
                <AdminPage />
              </AdminQueryScope>
            )}
          />
          <Route path="/moderation" element={<ModerationDeskPage />} />
          <Route path="/profile" element={<ProfileRedirect />} />
          <Route path="/tindra" element={<TindraPage />} />
          <Route path="/milestones" element={<MilestonesPage />} />
          <Route path="/intimate-videos" element={<IntimateVideosPage />} />
          {/* Paket S12 — Wochenrückblick und Erinnerungsbuch. /woche zeigt die
              Kennzahlen + Momente + Bilder der gewählten Woche; /erinnerungen
              öffnet das Erinnerungsbuch für scope=character|adventure|... */}
          <Route path="/woche" element={<WeekReviewPage />} />
          <Route path="/erinnerungen" element={<MemoryBookPage />} />
        </Route>
        {/* Chats nutzen die ChatShell mit eigenem Vollbild-Layout (kein AppLayout-Chrome). */}
        <Route
          path="/chat/:campaignId"
          element={(
            <RequireAuth>
              <ChatPage />
            </RequireAuth>
          )}
        />
        <Route
          path="/character-chat/:characterId"
          element={(
            <RequireAuth>
              <CharacterChatPage />
            </RequireAuth>
          )}
        />
        <Route
          path="/group-chat/:id"
          element={(
            <RequireAuth>
              <GroupChatPage />
            </RequireAuth>
          )}
        />
        <Route
          path="/table-round-chat/:roundId"
          element={(
            <RequireAuth>
              <TableRoundChatPage />
            </RequireAuth>
          )}
        />
        <Route
          path="/table-round-session/:roundId"
          element={(
            <RequireAuth>
              <TableRoundSessionPage />
            </RequireAuth>
          )}
        />
        {/* Catch-all: bewusst OHNE RequireAuth (wie schon der alte Redirect).
            Ein kaputter Link ist auch fuer Ausgeloggte ein kaputter Link — und
            der Onboarding-Redirect bleibt allein Sache von RequireAuth, in das
            erst der Dashboard-Button der Seite hineinlaeuft. */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
    </RouteErrorBoundary>
    </>
  );
}
